import React, { useEffect, useRef, useState, useCallback } from 'react';
import '../styles/CursorAnimation.css';

const CursorAnimation = () => {
  const [isMobile, setIsMobile] = useState(false);
  const rocketRef = useRef(null);
  const swirlContainerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastTimeRef = useRef(Date.now());
  const mousePositionRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const currentPositionRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const currentRotationRef = useRef(0);
  const lastTouchTimeRef = useRef(0);
  const lastTouchPositionRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const smoothAnimation = useCallback(() => {
    if (rocketRef.current) {
      const now = Date.now();
      const deltaTime = Math.min((now - lastTimeRef.current) / 16, 2); // Cap at 60fps equivalent
      lastTimeRef.current = now;

      const { x: mouseX, y: mouseY } = mousePositionRef.current;
      const { x: currentX, y: currentY } = currentPositionRef.current;

      // Smooth position interpolation
      const dx = mouseX - currentX;
      const dy = mouseY - currentY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Adaptive easing based on distance
      const easing = Math.min(0.15 * deltaTime, 1);
      const minSpeed = 0.5; // Minimum movement speed for responsiveness
      
      if (distance > minSpeed) {
        currentPositionRef.current.x += dx * easing;
        currentPositionRef.current.y += dy * easing;
        
        // Calculate target rotation
        const targetRotation = Math.atan2(dy, dx) + Math.PI / 2;
        
        // Normalize rotation difference
        let rotationDiff = targetRotation - currentRotationRef.current;
        while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
        while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
        
        // Smooth rotation interpolation
        currentRotationRef.current += rotationDiff * easing;
      }

      // Apply smooth transform
      rocketRef.current.style.transform = `translate(${currentPositionRef.current.x}px, ${currentPositionRef.current.y}px) rotate(${currentRotationRef.current}rad)`;
    }
    
    animationFrameRef.current = requestAnimationFrame(smoothAnimation);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      // Desktop: Rocket cursor with smooth animation
      const handleMouseMove = (event) => {
        mousePositionRef.current = { x: event.clientX, y: event.clientY };
      };

      window.addEventListener('mousemove', handleMouseMove);
      animationFrameRef.current = requestAnimationFrame(smoothAnimation);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    } else {
      // Mobile: Enhanced Swirl touch effect
      const touchThrottle = 32; // Minimum ms between swirls

      const createSwirl = (x, y, scale = 1) => {
        if (swirlContainerRef.current) {
          const now = Date.now();
          if (now - lastTouchTimeRef.current >= touchThrottle) {
            const swirl = document.createElement('div');
            swirl.className = 'crSwirl';
            swirl.style.left = `${x}px`;
            swirl.style.top = `${y}px`;
            swirl.style.transform = `translate(-50%, -50%) scale(${scale})`;
            swirlContainerRef.current.appendChild(swirl);

            lastTouchTimeRef.current = now;

            // Remove swirl after animation
            setTimeout(() => {
              if (swirl && swirl.parentElement) {
                swirl.style.opacity = '0';
                setTimeout(() => swirl.remove(), 300);
              }
            }, 700);
          }
        }
      };

      const handleTouchStart = (event) => {
        const touch = event.touches[0];
        lastTouchPositionRef.current = { x: touch.clientX, y: touch.clientY };
        createSwirl(touch.clientX, touch.clientY);
      };

      const handleTouchMove = (event) => {
        const touch = event.touches[0];
        const { x: lastX, y: lastY } = lastTouchPositionRef.current;
        const dx = touch.clientX - lastX;
        const dy = touch.clientY - lastY;

        // Prevent default only for horizontal scrolling
        if (Math.abs(dx) > Math.abs(dy)) {
          event.preventDefault();
        }

        createSwirl(touch.clientX, touch.clientY);
        lastTouchPositionRef.current = { x: touch.clientX, y: touch.clientY };
      };

      window.addEventListener('touchstart', handleTouchStart, { passive: false });
      window.addEventListener('touchmove', handleTouchMove, { passive: false });

      return () => {
        window.removeEventListener('touchstart', handleTouchStart);
        window.removeEventListener('touchmove', handleTouchMove);
      };
    }
  }, [isMobile, smoothAnimation]);

  return (
    <>
      {!isMobile && (
        <div ref={rocketRef} className="crRocket">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64">
            <path fill="#4a4a4a" d="M32 2s-16 10-16 30v20l16 10 16-10V32C48 12 32 2 32 2z"/>
            <path fill="#d32f2f" d="M32 2s-8 8-8 24v20l8 5 8-5V26C40 10 32 2 32 2z"/>
            <path fill="#f44336" d="M32 2s-4 6-4 18v20l4 3 4-3V20C36 8 32 2 32 2z"/>
            <circle fill="#ffc107" cx="32" cy="24" r="4"/>
            <path fill="#ffd54f" d="M28 46v6l4 2 4-2v-6l-4 2z"/>
          </svg>
          <div className="crFlame"></div>
        </div>
      )}
      {isMobile && <div ref={swirlContainerRef} className="crSwirlContainer"></div>}
    </>
  );
};

export default CursorAnimation;